import React, { useState } from 'react';
import { Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

type PowerUnit = 'W' | 'kW' | 'HP' | 'PS';
type CalculationType = 'voltage-current' | 'force-velocity' | 'torque-rpm' | 'conversion';

interface PowerState {
  value1: string;
  value2: string;
  result: number | null;
  fromUnit: PowerUnit;
  toUnit: PowerUnit;
}

function KalkulatorSnage() {
  const [calculationType, setCalculationType] = useState<CalculationType>('voltage-current');
  const [values, setValues] = useState<PowerState>({
    value1: '',
    value2: '',
    result: null,
    fromUnit: 'W',
    toUnit: 'kW'
  });

  const powerConversions = {
    'W': 1,
    'kW': 1000,
    'HP': 745.7,
    'PS': 735.49875
  };

  const calculateResult = () => {
    const num1 = parseFloat(values.value1);
    const num2 = parseFloat(values.value2);

    if (isNaN(num1) || (calculationType !== 'conversion' && isNaN(num2))) {
      return null;
    }

    switch (calculationType) {
      case 'voltage-current':
        // P = U × I (Snaga = Napon × Struja)
        return num1 * num2;
      case 'force-velocity':
        // P = F × v (Snaga = Sila × Brzina)
        return num1 * num2;
      case 'torque-rpm':
        // P = T × ω (Snaga = Moment × Kutna brzina)
        return (num1 * num2 * 2 * Math.PI) / 60;
      case 'conversion':
        // Konverzija između jedinica
        const baseValue = num1 * powerConversions[values.fromUnit];
        return baseValue / powerConversions[values.toUnit];
      default:
        return null;
    }
  };

  const getInputLabels = () => {
    switch (calculationType) {
      case 'voltage-current':
        return { value1: 'Napon (V)', value2: 'Struja (A)' };
      case 'force-velocity':
        return { value1: 'Sila (N)', value2: 'Brzina (m/s)' };
      case 'torque-rpm':
        return { value1: 'Moment (Nm)', value2: 'Brzina vrtnje (RPM)' };
      case 'conversion':
        return { value1: 'Vrijednost', value2: '' };
      default:
        return { value1: 'Vrijednost 1', value2: 'Vrijednost 2' };
    }
  };

  const getResultLabel = () => {
    switch (calculationType) {
      case 'voltage-current':
        return 'Električna snaga:';
      case 'force-velocity':
        return 'Mehanička snaga:';
      case 'torque-rpm':
        return 'Snaga rotacije:';
      case 'conversion':
        return 'Rezultat:';
      default:
        return 'Rezultat:';
    }
  };

  const handleInputChange = (field: keyof PowerState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
      result: null
    }));
  };

  const handleCalculate = () => {
    const result = calculateResult();
    setValues(prev => ({
      ...prev,
      result: result !== null ? parseFloat(result.toFixed(3)) : null
    }));
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Snage",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Online kalkulator za izračun električne i mehaničke snage. Konvertirajte između različitih jedinica snage (W, kW, HP, PS).",
    "url": "https://online-kalkulator.com/kalkulator-snage",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun električne snage",
      "Izračun mehaničke snage",
      "Konverzija jedinica snage",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Snage | Izračun Električne i Mehaničke Snage"
        description="Online kalkulator za izračun električne i mehaničke snage. Konvertirajte između različitih jedinica snage (W, kW, HP, PS) brzo i jednostavno." 
        canonicalUrl="/kalkulator-snage"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Snage</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Calculation Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Odaberite vrstu izračuna:
                </label>
                <select
                  value={calculationType}
                  onChange={(e) => setCalculationType(e.target.value as CalculationType)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="voltage-current">Električna snaga (P = U × I)</option>
                  <option value="force-velocity">Mehanička snaga (P = F × v)</option>
                  <option value="torque-rpm">Snaga rotacije (P = T × ω)</option>
                  <option value="conversion">Konverzija jedinica snage</option>
                </select>
              </div>

              {/* Input Fields */}
              <div className={calculationType === 'conversion' ? 'space-y-4' : 'grid grid-cols-2 gap-4'}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {getInputLabels().value1}
                  </label>
                  <input
                    type="number"
                    value={values.value1}
                    onChange={(e) => handleInputChange('value1', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite vrijednost"
                  />
                </div>
                
                {calculationType === 'conversion' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Iz jedinice</label>
                      <select
                        value={values.fromUnit}
                        onChange={(e) => handleInputChange('fromUnit', e.target.value as PowerUnit)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="W">Vat (W)</option>
                        <option value="kW">Kilovat (kW)</option>
                        <option value="HP">Konjska snaga (HP)</option>
                        <option value="PS">Metrička konjska snaga (PS)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">U jedinicu</label>
                      <select
                        value={values.toUnit}
                        onChange={(e) => handleInputChange('toUnit', e.target.value as PowerUnit)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="W">Vat (W)</option>
                        <option value="kW">Kilovat (kW)</option>
                        <option value="HP">Konjska snaga (HP)</option>
                        <option value="PS">Metrička konjska snaga (PS)</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {getInputLabels().value2}
                    </label>
                    <input
                      type="number"
                      value={values.value2}
                      onChange={(e) => handleInputChange('value2', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Unesite vrijednost"
                    />
                  </div>
                )}
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Izračunaj
              </button>

              {/* Result */}
              {values.result !== null && (
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{getResultLabel()}</span>
                    <span className="text-xl font-bold text-blue-600">
                      {values.result} {calculationType === 'conversion' ? values.toUnit : 'W'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Snage</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator snage je svestran alat koji vam omogućuje izračun različitih vrsta snage i
                  konverziju između različitih jedinica snage. Bilo da radite s električnom snagom,
                  mehaničkom snagom ili trebate konvertirati između različitih jedinica, ovaj kalkulator
                  će vam pomoći da brzo dođete do rezultata.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Dostupni Izračuni</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Električna snaga (P = U × I) - izračun snage iz napona i struje</li>
                  <li>Mehanička snaga (P = F × v) - izračun snage iz sile i brzine</li>
                  <li>Snaga rotacije (P = T × ω) - izračun snage iz momenta i kutne brzine</li>
                  <li>Konverzija između različitih jedinica snage (W, kW, HP, PS)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Jedinice Snage</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Vat (W) - osnovna SI jedinica za snagu</li>
                  <li>Kilovat (kW) - 1 kW = 1000 W</li>
                  <li>Konjska snaga (HP) - 1 HP ≈ 745.7 W</li>
                  <li>Metrička konjska snaga (PS) - 1 PS ≈ 735.49875 W</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Odaberite vrstu izračuna iz padajućeg izbornika</li>
                  <li>Unesite potrebne vrijednosti u odgovarajuća polja</li>
                  <li>Za konverziju jedinica, odaberite početnu i željenu jedinicu</li>
                  <li>Kliknite "Izračunaj" za dobivanje rezultata</li>
                  <li>Rezultat će biti prikazan s odgovarajućom jedinicom</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Svi rezultati su zaokruženi na tri decimale</li>
                  <li>Za decimalne brojeve koristite točku kao decimalni separator</li>
                  <li>Provjerite jesu li ulazne vrijednosti u ispravnim jedinicama</li>
                  <li>Za električnu snagu, napon mora biti u voltima (V), a struja u amperima (A)</li>
                  <li>Za mehaničku snagu, sila mora biti u njutnima (N), a brzina u metrima po sekundi (m/s)</li>
                  <li>Za snagu rotacije, moment mora biti u njutn-metrima (Nm), a brzina vrtnje u okretajima po minuti (RPM)</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorSnage;